import { Box, type SxProps, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { env } from '@repo/env';
import type { MobileOrDesktop } from '@repo/utils/deviceType/types';
import { CalculatorDisclaimer } from './CalculatorDisclaimer';
import type { CalculatorSectionVariant } from './types';

type CalculatorSectionLayoutProps = {
  children: ReactNode;
  containerSX?: SxProps;
  deviceType?: MobileOrDesktop;
  variant: CalculatorSectionVariant;
  title: string;
};

function CalculatorTitle({ title }: { title: string }) {
  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'row',
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
        gap: '.5rem',
        justifyContent: 'center',
        maxWidth: '100%',
        minWidth: 0,
        width: '100%',
      }}
    >
      <Box
        component="img"
        loading="lazy"
        alt=""
        src={`${env.NEXT_PUBLIC_NW_URL}/cdn/banking/icons/bar-graph-logo.svg`}
        sx={{
          display: {
            xs: 'none',
            sm: 'block',
          },
          fontSize: {
            md: '2rem',
          },
          lineHeight: {
            xs: '133%',
            md: '32px',
          },
        }}
      />
      <Typography
        variant="textLargeBold"
        sx={{
          color: 'neutral.darkest',
          maxWidth: '100%',
          minWidth: 0,
          textAlign: 'center',
        }}
      >
        {title}
      </Typography>
    </Box>
  );
}

function CalculatorDisclaimerText() {
  return (
    <Typography
      variant="textSmall"
      component="p"
      sx={{
        color: 'neutral.darkest',
        textAlign: 'center',
        letterSpacing: '0.065px',
        lineHeight: '154%',
      }}
    >
      The calculator information provided is for educational purposes only. Projections generated
      are hypothetical and not a guarantee of future results.
    </Typography>
  );
}

export function CalculatorSectionLayout({
  children,
  containerSX = {},
  deviceType = 'desktop',
  variant,
  title,
}: CalculatorSectionLayoutProps) {
  return (
    <Box
      data-testid="calculator-section"
      sx={{
        backgroundColor: 'blues.extraLight',
        marginLeft: 'calc(-50vw + 50%)',
        marginRight: 'calc(-50vw + 50%)',
        paddingY: {
          xs: '1.5rem',
          sm: '2rem',
          md: '2rem',
        },
        width: '100vw',
        ...containerSX,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '2rem',
          marginX: 'auto',
          maxWidth: 'xl',
          paddingX: {
            xs: '1.5rem',
            sm: '2rem',
            md: '7rem',
          },
          width: '100%',
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: '2rem',
          }}
        >
          <CalculatorTitle title={title} />
        </Box>
        {children}
        <Box
          data-testid="calculator-disclaimer"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0,
          }}
        >
          <CalculatorDisclaimerText />
          <CalculatorDisclaimer deviceType={deviceType} variant={variant} />
        </Box>
      </Box>
    </Box>
  );
}
